/**
 * Loads a script.
 * @param {string} src - Url of script to load.
 * @returns {Promise}
 */
function loadScript(src) {
  return new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = src;
    s.onload = resolve;
    s.onerror = reject;
    document.body.appendChild(s);
  });
}

/* global DetailsDisclosure, trapFocus, removeTrapFocus */

if (!customElements.get('cart-drawer-disclosure')) {
  class CartDrawerDisclosure extends DetailsDisclosure {
    constructor() {
      super();
      this.openBtn = this.querySelector('summary');
      this.closeBtn = this.querySelector('.js-close');
      this.overlay = document.getElementById('cart-drawer').querySelector('.overlay');
    }

    /**
     * Handles 'click' events on the custom element.
     * @param {object} evt - Event object.
     */
    handleClick(evt) {
      if (!evt.target.matches('.js-close')) return;
      this.close();
    }

    /**
     * Opens the details element.
     */
    open() {
      this.overlay.classList.add('is-visible');
      super.open();
      trapFocus(this);

      // Create event handler variables (so the bound event listeners can be removed).
      this.clickHandler = this.clickHandler || this.handleClick.bind(this);
      this.keyupHandler = (evt) => evt.key === 'Escape' && this.close();

      // Add event listeners (for while disclosure is open).
      this.addEventListener('click', this.clickHandler);
      this.addEventListener('keyup', this.keyupHandler);
      this.overlay.addEventListener('click', this.clickHandler);
    }

    /**
     * Closes the details element.
     */
    close() {
      this.overlay.classList.remove('is-visible');
      super.close();
      removeTrapFocus(this.openBtn);

      this.removeEventListener('click', this.clickHandler);
      this.removeEventListener('keyup', this.keyupHandler);
      this.overlay.removeEventListener('click', this.clickHandler);
    }
  }

  customElements.define('cart-drawer-disclosure', CartDrawerDisclosure);
}

/* global SideDrawer */

if (!customElements.get('cart-drawer')) {
  class CartDrawer extends SideDrawer {
    constructor() {
      super();

      this.cartSummary = this.querySelector('.cart-drawer__summary');
      this.init();
      this.bindEvents();
    }

    disconnectedCallback() {
      if (this.countObserver) this.countObserver.disconnect();
      clearInterval(this.deliveryTimer);
      document.removeEventListener('dispatch:cart-drawer:refresh', this.refreshHandler);
      document.removeEventListener('on:bfcache:load-restore', this.refreshHandler);
      document.removeEventListener('dispatch:cart-drawer:open', this.openDrawerViaEventHandler);
      document.removeEventListener('dispatch:cart-drawer:close', this.closeDrawerViaEventHandler);

      if (Shopify.designMode) {
        document.removeEventListener('shopify:section:select', this.sectionSelectHandler);
        document.removeEventListener('shopify:section:deselect', this.sectionDeselectHandler);
      }

      if (this.dcbLoadedHandler) {
        document.removeEventListener('shopify:payment_button:loaded', this.dcbLoadedHandler);
      }
    }

    init() {
      const cartIcon = document.getElementById('cart-icon');
      if (cartIcon) {
        cartIcon.setAttribute('role', 'button');
        cartIcon.setAttribute('aria-haspopup', 'dialog');

        cartIcon.addEventListener('click', (evt) => {
          evt.preventDefault();
          this.open(cartIcon);
        });

        cartIcon.addEventListener('keydown', (evt) => {
          if (evt.key !== ' ') return;
          evt.preventDefault();
          this.open(cartIcon);
        });
      }
    }

    bindEvents() {
      if (Shopify.designMode) {
        this.sectionSelectHandler = this.handleSectionSelect.bind(this);
        this.sectionDeselectHandler = this.handleSectionDeselect.bind(this);
        document.addEventListener('shopify:section:select', this.sectionSelectHandler);
        document.addEventListener('shopify:section:deselect', this.sectionDeselectHandler);
      }

      this.openDrawerViaEventHandler = this.handleDrawerOpenViaEvent.bind(this);
      this.closeDrawerViaEventHandler = this.close.bind(this, null);
      document.addEventListener('dispatch:cart-drawer:open', this.openDrawerViaEventHandler);
      document.addEventListener('dispatch:cart-drawer:close', this.closeDrawerViaEventHandler);

      if (this.cartSummary.classList.contains('cart-drawer--checkout--sticky-true')) {
        this.dcbLoadedHandler = this.dcbLoadedHandler || CartDrawer.recalculateCssVarHeights;
        document.addEventListener('shopify:payment_button:loaded', this.dcbLoadedHandler);
      }

      this.refreshHandler = this.refresh.bind(this);
      document.addEventListener('dispatch:cart-drawer:refresh', this.refreshHandler);
      document.addEventListener('on:bfcache:load-restore', this.refreshHandler);

      // The header is not re-rendered when the cart changes, so copy the item count from the cart items.
      // Watch the whole drawer: the cart items element itself is replaced when a product is added.
      if ('MutationObserver' in window) {
        this.countObserver = new MutationObserver(() => {
          cancelAnimationFrame(this.countFrame);
          this.countFrame = requestAnimationFrame(() => {
            this.updateCount();
            this.updateDelivery();
          });
        });
        this.countObserver.observe(this, { childList: true, subtree: true });
      }
    }

    /**
     * Shows "order within Xh and get it tomorrow" only when that is true: on a business day, before the
     * cut-off, with tomorrow also a business day (national holidays of PT or ES). Uses the shop's time zone.
     */
    updateDelivery() {
      const line = this.querySelector('[data-ncd-delivery]');
      if (!line) return;

      const { country, tz, template } = line.dataset;
      const cutoff = Number(line.dataset.cutoff);
      const now = CartDrawer.zonedNow(tz);
      const today = new Date(Date.UTC(now.year, now.month - 1, now.day));
      const tomorrow = new Date(today);
      tomorrow.setUTCDate(tomorrow.getUTCDate() + 1);

      const minutesLeft = cutoff * 60 - (now.hour * 60 + now.minute);
      // Orders ship from Portugal: the warehouse must work today and tomorrow, and tomorrow must be a business
      // day where the customer is
      const show = minutesLeft > 0
        && CartDrawer.isBusinessDay(today, 'PT')
        && CartDrawer.isBusinessDay(tomorrow, 'PT')
        && CartDrawer.isBusinessDay(tomorrow, country);

      if (show) {
        const hours = Math.floor(minutesLeft / 60);
        const minutes = minutesLeft % 60;
        const time = hours > 0 ? `${hours}h ${String(minutes).padStart(2, '0')}min` : `${minutes} min`;
        const html = template.replace('[time]', time);
        const text = line.querySelector('.js-ncd-delivery-text');
        // Compare with what we wrote, not with innerHTML (the browser re-serialises entities)
        if (text && text.dataset.html !== html) {
          text.dataset.html = html;
          text.innerHTML = html;
        }
      }
      if (line.hidden === show) line.hidden = !show;
    }

    /**
     * Current date and time in a time zone.
     * @param {string} timeZone - IANA time zone.
     * @returns {{year: number, month: number, day: number, hour: number, minute: number}}
     */
    static zonedNow(timeZone) {
      const parts = new Intl.DateTimeFormat('en-GB', {
        timeZone, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false
      }).formatToParts(new Date()).reduce((acc, part) => {
        acc[part.type] = part.value;
        return acc;
      }, {});

      return {
        year: Number(parts.year),
        month: Number(parts.month),
        day: Number(parts.day),
        hour: Number(parts.hour === '24' ? '0' : parts.hour),
        minute: Number(parts.minute)
      };
    }

    /**
     * @param {Date} date - UTC midnight of the day to check.
     * @param {string} country - 'PT' or 'ES'.
     * @returns {boolean}
     */
    static isBusinessDay(date, country) {
      const weekday = date.getUTCDay();
      if (weekday === 0 || weekday === 6) return false;
      return !CartDrawer.nationalHolidays(date.getUTCFullYear(), country).has(date.toISOString().slice(0, 10));
    }

    /**
     * National public holidays.
     * @param {number} year - Year.
     * @param {string} country - 'PT' or 'ES'.
     * @returns {Set<string>} Dates as YYYY-MM-DD.
     */
    static nationalHolidays(year, country) {
      CartDrawer.holidayCache = CartDrawer.holidayCache || {};
      const cacheKey = `${country}-${year}`;
      if (CartDrawer.holidayCache[cacheKey]) return CartDrawer.holidayCache[cacheKey];

      // Easter Sunday (anonymous Gregorian algorithm)
      const a = year % 19;
      const b = Math.floor(year / 100);
      const c = year % 100;
      const d = Math.floor(b / 4);
      const e = b % 4;
      const f = Math.floor((b + 8) / 25);
      const g = Math.floor((b - f + 1) / 3);
      const h = (19 * a + b - d - g + 15) % 30;
      const i = Math.floor(c / 4);
      const k = c % 4;
      const l = (32 + 2 * e + 2 * i - h - k) % 7;
      const m = Math.floor((a + 11 * h + 22 * l) / 451);
      const month = Math.floor((h + l - 7 * m + 114) / 31);
      const day = ((h + l - 7 * m + 114) % 31) + 1;
      const easter = Date.UTC(year, month - 1, day);
      const fromEaster = (offset) => new Date(easter + offset * 86400000).toISOString().slice(0, 10);

      const fixed = country === 'ES'
        ? ['01-01', '01-06', '05-01', '08-15', '10-12', '11-01', '12-06', '12-08', '12-25']
        : ['01-01', '04-25', '05-01', '06-10', '08-15', '10-05', '11-01', '12-01', '12-08', '12-25'];
      const movable = country === 'ES' ? [fromEaster(-2)] : [fromEaster(-2), fromEaster(0), fromEaster(60)];

      CartDrawer.holidayCache[cacheKey] = new Set([...fixed.map((md) => `${year}-${md}`), ...movable]);
      return CartDrawer.holidayCache[cacheKey];
    }

    /**
     * Updates the item count next to the drawer title.
     */
    updateCount() {
      const badge = this.querySelector('.js-ncd-header-count');
      if (!badge) return;

      const source = this.querySelector('.js-ncd-count');
      const count = source ? Number(source.dataset.count) : 0;
      const number = badge.querySelector('.js-ncd-count-number') || badge;

      // Only write when something changed: every write is a mutation the observer would see again
      if (number.textContent !== String(count)) number.textContent = count;
      if (badge.hidden !== (count === 0)) badge.hidden = count === 0;
    }

    /**
     * Handle when the section is selected in the Theme Editor
     * @param {object} evt - Event object.
     */
    handleSectionSelect(evt) {
      if (evt.target === this.closest('.shopify-section')) this.open();
    }

    /**
     * Handle when the section is de-selected in the Theme Editor
     * @param {object} evt - Event object.
     */
    handleSectionDeselect(evt) {
      if (evt.target === this.closest('.shopify-section')) this.close();
    }

    /**
     * Handle when the drawer is opened via an event
     * @param {object} evt - Event object.
     */
    handleDrawerOpenViaEvent(evt) {
      this.open(evt.detail ? evt.detail.opener : null);
    }

    /**
     * Opens the drawer.
     * @param {Element} [opener] - Element that triggered opening of the drawer.
     * @param {Element} [elementToFocus] - Element to focus after drawer opened.
     */
    open(opener, elementToFocus) {
      // Get the quick add drawer web component, if it's currently open and close it
      const quickAddDrawer = document.querySelector('quick-add-drawer[aria-hidden="false"]');
      const overlay = document.querySelector('.js-overlay.is-visible');

      if (quickAddDrawer) {
        if (overlay) overlay.style.transitionDelay = '200ms';
        quickAddDrawer.close();
      }

      // If the cart drawer is open, wait a few ms for a more optimal ux/animation
      this.updateDelivery();
      clearInterval(this.deliveryTimer);
      this.deliveryTimer = setInterval(() => {
        if (this.getAttribute('open') === null) {
          clearInterval(this.deliveryTimer);
          return;
        }
        this.updateDelivery();
      }, 60000);

      setTimeout(() => {
        // The recommendations wait to scroll into view before loading, which never happens inside the
        // off-canvas drawer, so load them when the drawer opens.
        if (typeof window.ncdUpdateCartRecommendations === 'function') {
          window.ncdUpdateCartRecommendations(this, this.querySelector('#cart-recommendations'));
        }

        super.open(opener, elementToFocus, () => {
          if (this.cartSummary.classList.contains('cart-drawer--checkout--sticky-true')) {
            CartDrawer.recalculateCssVarHeights();
          }
        });

        if (this.cartSummary.classList.contains('cart-drawer--checkout--sticky-true')) {
          CartDrawer.recalculateCssVarHeights();
        }

        if (overlay) overlay.style.transitionDelay = '';
      }, quickAddDrawer ? 200 : 0);
    }

    /**
     * Attempts to refresh any cart items (if present). Failing that, it refreshes the entire cart
     * drawer
     * @param {boolean} [dontRefreshCartItems=false] - Prevents the refresh of cart items, and does
     * a straight refresh of the whole cart
     */
    async refresh(dontRefreshCartItems) {
      try {
        const cartItems = this.querySelector('cart-items');
        if (cartItems && !dontRefreshCartItems) {
          cartItems.refresh();
        } else {
          const response = this.getSectionsToRender().map((section) => section.section);
          const cartResponse = await fetch(`?sections=${response.join(',')}`);
          const sections = await cartResponse.json();
          this.renderContents({ sections }, false);
          CartDrawer.recalculateCssVarHeights();
        }
      } catch (error) {

        this.dispatchEvent(new CustomEvent('on:cart:error', {
          bubbles: true,
          detail: {
            error: this.errorMsg?.textContent
          }
        }));
      }
    }

    /**
     * Renders the contents of the specified sections to update.
     * @param {object} data - Cart data object.
     * @param {boolean} [openDrawer=true] - Open the cart drawer after rendering sections.
     */
    async renderContents(data, openDrawer = true) {
      if (!this.scriptsLoaded) {
        if (!document.querySelector(`script[src="${theme.scripts.cartItems}"]`)) {
          await loadScript(theme.scripts.cartItems);
        }

        if (!document.querySelector(`script[src="${theme.scripts.shippingCalculator}"]`) && this.dataset.shippingCalculator) {
          await loadScript(theme.scripts.countryProvinceSelector);
          await loadScript(theme.scripts.shippingCalculator);
        }

        this.scriptsLoaded = true;
      }

      this.getSectionsToRender().forEach((section) => {
        const el = document.getElementById(section.id);
        if (el) {
          el.innerHTML = CartDrawer.getElementHTML(
            data.sections[section.section],
            section.selector
          );
        }
      });

      if (typeof window.ncdUpdateCartRecommendations === 'function') {
        window.ncdUpdateCartRecommendations(this, this.querySelector('#cart-recommendations'));
      }

      if (openDrawer && this.getAttribute('open') === null) {
        setTimeout(() => this.open());
      } else if (this.getAttribute('open') !== null && typeof trapFocus === 'function') {
        // The drawer was re-rendered while open (e.g. "+ Adicionar"): keep the focus inside the dialog, on the
        // line that was just added when there is one
        const addedVariant = data && (data.variant_id || data.id);
        const addedLine = addedVariant ? this.querySelector(`.cart-item[data-variant-id="${addedVariant}"] .js-item-name`) : null;
        const focusTarget = addedLine || this.querySelector('.js-close-drawer');
        if (focusTarget) trapFocus(this, focusTarget);
      }
    }

    /**
     * Returns an array of objects containing required section details.
     * @returns {Array}
     */
    getSectionsToRender() {
      return [
        {
          id: 'cart-drawer',
          section: this.closest('.shopify-section').id.replace('shopify-section-', ''),
          selector: 'cart-drawer'
        },
        {
          id: 'cart-icon-bubble',
          section: 'cart-icon-bubble',
          selector: '.shopify-section'
        }
      ];
    }

    /**
     * Dispatches an event to recalculate the data-css-var-heights on the page. Useful for
     * updating the height of the sticky cart buttons
     */
    static recalculateCssVarHeights() {
      window.requestAnimationFrame(() => {
        document.dispatchEvent(new CustomEvent('on:css-var-height:update'));
      });
    }

    /**
     * Gets the innerHTML of an element.
     * @param {string} html - Section HTML.
     * @param {string} selector - CSS selector for the element.
     * @returns {string}
     */
    static getElementHTML(html, selector) {
      const tmpl = document.createElement('template');
      tmpl.innerHTML = html;

      return tmpl.content.querySelector(selector).innerHTML;
    }
  }

  customElements.define('cart-drawer', CartDrawer);
}
